import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore'
import { db } from '@/firebase'
import { useAuthStore } from './auth'

export const useCompanyStore = defineStore('company', () => {
  const authStore = useAuthStore()

  const company = ref(null)
  const teamMembers = ref([])
  const pendingInvites = ref([])
  const loading = ref(false)
  const error = ref(null)

  // Computed
  const isCompanyAdmin = computed(() => {
    return authStore.userData?.accountType === 'company_admin'
  })

  const isTeamMember = computed(() => {
    return authStore.userData?.accountType === 'team_member'
  })

  const hasCompany = computed(() => {
    return !!authStore.userData?.companyId
  })

  const availableSeats = computed(() => {
    if (!company.value) return 0
    const total = company.value.billing?.seats || 0
    const used = company.value.billing?.usedSeats || 0
    return total - used
  })

  /**
   * Create a new company
   */
  const createCompany = async (companyData) => {
    if (!authStore.user?.uid) {
      throw new Error('User not authenticated')
    }

    loading.value = true
    error.value = null

    try {
      const newCompany = {
        name: companyData.name.trim(),
        adminUserId: authStore.user.uid,
        settings: {
          sharedCustomers: companyData.sharedCustomers ?? true,
          sharedHistory: companyData.sharedHistory ?? false,
          requireApproval: companyData.requireApproval ?? false
        },
        billing: {
          plan: 'team',
          seats: companyData.seats || 10,
          usedSeats: 1 // Admin counts as first seat
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }

      const docRef = await addDoc(collection(db, 'companies'), newCompany)

      // Update user to be company admin
      const userRef = doc(db, 'users', authStore.user.uid)
      await updateDoc(userRef, {
        accountType: 'company_admin',
        companyId: docRef.id,
        companyRole: 'admin',
        joinedCompanyAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })

      // Refresh user data
      await authStore.refreshUserData()

      company.value = {
        id: docRef.id,
        ...newCompany,
        createdAt: new Date(),
        updatedAt: new Date()
      }

      return company.value
    } catch (err) {
      console.error('Error creating company:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch company details
   */
  const fetchCompany = async (companyId) => {
    if (!companyId) {
      companyId = authStore.userData?.companyId
    }

    if (!companyId) {
      return null
    }

    loading.value = true
    error.value = null

    try {
      const docRef = doc(db, 'companies', companyId)
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        company.value = {
          id: docSnap.id,
          ...docSnap.data()
        }
        return company.value
      } else {
        throw new Error('Company not found')
      }
    } catch (err) {
      console.error('Error fetching company:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Update company settings
   */
  const updateCompany = async (companyId, updates) => {
    loading.value = true
    error.value = null

    try {
      const docRef = doc(db, 'companies', companyId)
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      })

      // Update local state
      if (company.value?.id === companyId) {
        company.value = {
          ...company.value,
          ...updates,
          updatedAt: new Date()
        }
      }
    } catch (err) {
      console.error('Error updating company:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch team members
   */
  const fetchTeamMembers = async (companyId) => {
    if (!companyId) {
      companyId = authStore.userData?.companyId
    }

    if (!companyId) {
      return []
    }

    loading.value = true
    error.value = null

    try {
      const q = query(
        collection(db, 'users'),
        where('companyId', '==', companyId),
        orderBy('joinedCompanyAt', 'desc')
      )

      const snapshot = await getDocs(q)
      teamMembers.value = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))

      return teamMembers.value
    } catch (err) {
      console.error('Error fetching team members:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Send team invitation
   */
  const sendInvite = async (inviteData) => {
    if (!authStore.user?.uid || !authStore.userData?.companyId) {
      throw new Error('User not authenticated or not part of a company')
    }

    if (!isCompanyAdmin.value) {
      throw new Error('Only company admins can send invites')
    }

    loading.value = true
    error.value = null

    try {
      // Generate secure invite token
      const inviteToken = generateSecureToken()

      const newInvite = {
        companyId: authStore.userData.companyId,
        companyName: company.value?.name || 'Your Team',
        invitedBy: authStore.user.uid,
        invitedByName: authStore.userData?.name || 'Team Admin',

        firstName: inviteData.firstName.trim(),
        lastName: inviteData.lastName.trim(),
        email: inviteData.email?.trim() || null,
        phone: inviteData.phone?.trim() || null,

        inviteToken,
        inviteType: inviteData.inviteType, // 'sms' | 'email'
        status: 'pending',

        sentAt: serverTimestamp(),
        expiresAt: Timestamp.fromDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)), // 7 days
        acceptedAt: null,

        createdAt: serverTimestamp()
      }

      const docRef = await addDoc(collection(db, 'teamInvites'), newInvite)

      // Add to local state
      pendingInvites.value.unshift({
        id: docRef.id,
        ...newInvite,
        sentAt: new Date(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        createdAt: new Date()
      })

      return {
        id: docRef.id,
        inviteToken,
        inviteUrl: `${window.location.origin}/join/${inviteToken}`
      }
    } catch (err) {
      console.error('Error sending invite:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch pending invites
   */
  const fetchPendingInvites = async (companyId) => {
    if (!companyId) {
      companyId = authStore.userData?.companyId
    }

    if (!companyId) {
      return []
    }

    loading.value = true
    error.value = null

    try {
      const q = query(
        collection(db, 'teamInvites'),
        where('companyId', '==', companyId),
        where('status', '==', 'pending'),
        orderBy('sentAt', 'desc')
      )

      const snapshot = await getDocs(q)
      pendingInvites.value = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))

      return pendingInvites.value
    } catch (err) {
      console.error('Error fetching invites:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Cancel/revoke an invitation
   */
  const cancelInvite = async (inviteId) => {
    loading.value = true
    error.value = null

    try {
      const docRef = doc(db, 'teamInvites', inviteId)
      await updateDoc(docRef, {
        status: 'cancelled',
        cancelledAt: serverTimestamp()
      })

      // Remove from local state
      pendingInvites.value = pendingInvites.value.filter(inv => inv.id !== inviteId)
    } catch (err) {
      console.error('Error cancelling invite:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Accept team invitation
   */
  const acceptInvite = async (inviteToken) => {
    if (!authStore.user?.uid) {
      throw new Error('User not authenticated')
    }

    loading.value = true
    error.value = null

    try {
      // Find invite by token
      const q = query(
        collection(db, 'teamInvites'),
        where('inviteToken', '==', inviteToken),
        where('status', '==', 'pending')
      )

      const snapshot = await getDocs(q)

      if (snapshot.empty) {
        throw new Error('Invalid or expired invitation')
      }

      const inviteDoc = snapshot.docs[0]
      const inviteData = inviteDoc.data()

      // Check if expired
      const now = new Date()
      const expiresAt = inviteData.expiresAt.toDate()
      if (now > expiresAt) {
        throw new Error('This invitation has expired')
      }

      // Update user to join company
      const userRef = doc(db, 'users', authStore.user.uid)
      await updateDoc(userRef, {
        accountType: 'team_member',
        companyId: inviteData.companyId,
        companyRole: 'member',
        joinedCompanyAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })

      // Mark invite as accepted
      const inviteRef = doc(db, 'teamInvites', inviteDoc.id)
      await updateDoc(inviteRef, {
        status: 'accepted',
        acceptedAt: serverTimestamp(),
        acceptedBy: authStore.user.uid
      })

      // Update company seat count
      const companyRef = doc(db, 'companies', inviteData.companyId)
      const companySnap = await getDoc(companyRef)
      if (companySnap.exists()) {
        const currentUsed = companySnap.data().billing?.usedSeats || 0
        await updateDoc(companyRef, {
          'billing.usedSeats': currentUsed + 1,
          updatedAt: serverTimestamp()
        })
      }

      // Refresh user data
      await authStore.refreshUserData()

      return {
        companyId: inviteData.companyId,
        companyName: inviteData.companyName
      }
    } catch (err) {
      console.error('Error accepting invite:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Remove team member
   */
  const removeMember = async (userId) => {
    if (!isCompanyAdmin.value) {
      throw new Error('Only admins can remove team members')
    }

    loading.value = true
    error.value = null

    try {
      const userRef = doc(db, 'users', userId)
      await updateDoc(userRef, {
        accountType: 'individual',
        companyId: null,
        companyRole: null,
        leftCompanyAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })

      // Update company seat count
      if (company.value) {
        const companyRef = doc(db, 'companies', company.value.id)
        const currentUsed = company.value.billing?.usedSeats || 0
        await updateDoc(companyRef, {
          'billing.usedSeats': Math.max(0, currentUsed - 1),
          updatedAt: serverTimestamp()
        })
      }

      // Remove from local state
      teamMembers.value = teamMembers.value.filter(member => member.id !== userId)
    } catch (err) {
      console.error('Error removing member:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Leave company (for team members)
   */
  const leaveCompany = async () => {
    if (!authStore.user?.uid || !authStore.userData?.companyId) {
      throw new Error('Not part of a company')
    }

    if (isCompanyAdmin.value) {
      throw new Error('Company admins cannot leave. Please transfer ownership or delete the company.')
    }

    loading.value = true
    error.value = null

    try {
      const userRef = doc(db, 'users', authStore.user.uid)
      const companyId = authStore.userData.companyId

      await updateDoc(userRef, {
        accountType: 'individual',
        companyId: null,
        companyRole: null,
        leftCompanyAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })

      // Update company seat count
      const companyRef = doc(db, 'companies', companyId)
      const companySnap = await getDoc(companyRef)
      if (companySnap.exists()) {
        const currentUsed = companySnap.data().billing?.usedSeats || 0
        await updateDoc(companyRef, {
          'billing.usedSeats': Math.max(0, currentUsed - 1),
          updatedAt: serverTimestamp()
        })
      }

      // Refresh user data
      await authStore.refreshUserData()

      // Clear local state
      company.value = null
      teamMembers.value = []
      pendingInvites.value = []
    } catch (err) {
      console.error('Error leaving company:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Helper: Generate secure invite token
   */
  const generateSecureToken = () => {
    const array = new Uint8Array(32)
    crypto.getRandomValues(array)
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('')
  }

  return {
    // State
    company,
    teamMembers,
    pendingInvites,
    loading,
    error,

    // Computed
    isCompanyAdmin,
    isTeamMember,
    hasCompany,
    availableSeats,

    // Actions
    createCompany,
    fetchCompany,
    updateCompany,
    fetchTeamMembers,
    sendInvite,
    fetchPendingInvites,
    cancelInvite,
    acceptInvite,
    removeMember,
    leaveCompany
  }
})
